import { useEffect, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReferralDashboard, ReferralPayoutMethod, ReferralProgram } from '@zeroth/referral-contract';
import { apiRequest } from '../lib/api';

const dashboardKey = (userId: string | null | undefined) => ['partner-portal', 'dashboard', userId];

/** Loads the partner dashboard and enrolls the signed-in user automatically on first visit. */
export function usePartnerDashboard() {
  const { userId, getToken } = useAuth();
  const client = useQueryClient();
  const key = dashboardKey(userId);
  const query = useQuery({
    queryKey: key,
    enabled: !!userId,
    queryFn: async () =>
      (await apiRequest<{ data: ReferralDashboard }>(getToken, 'GET', '/api/referrals/dashboard')).data,
  });
  const join = useMutation({
    mutationFn: async () =>
      (await apiRequest<{ data: ReferralDashboard }>(getToken, 'POST', '/api/referrals/join', {})).data,
    onSuccess: (data) => client.setQueryData(key, data),
  });
  const joinRequested = useRef(false);
  const needsJoin = query.isSuccess && !query.data.partner;
  useEffect(() => {
    if (needsJoin && !joinRequested.current) {
      joinRequested.current = true;
      join.mutate();
    }
  }, [needsJoin, join]);
  return { query, join, data: query.data?.partner ? query.data : undefined };
}

export function usePayoutDetails() {
  const { userId, getToken } = useAuth();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { method: ReferralPayoutMethod; handle: string }) =>
      (await apiRequest<{ data: ReferralDashboard }>(getToken, 'PATCH', '/api/referrals/payout-details', input))
        .data,
    onSuccess: (data) => client.setQueryData(dashboardKey(userId), data),
  });
}

/** The public program definition (tiers, discount, payout rules). No sign-in needed. */
export function useProgram() {
  return useQuery({
    queryKey: ['partner-portal', 'program'],
    staleTime: 5 * 60 * 1000,
    queryFn: async () =>
      (await apiRequest<{ data: ReferralProgram }>(async () => null, 'GET', '/api/referrals/program')).data,
  });
}
